import { ComputerIcon, css, MobileIcon, Mode, styled, TabletIcon, useModeContext } from 'canoe';
import React from 'react';

import { useEventing } from 'commons/eventing';
import { Toggle } from 'craft/containers/Toolbox/components';
import { useSetViewportBreakpoint, useViewportBreakpoint } from 'craft/state';
import { MediaBreakpoint } from 'craft/types';
import Tooltip from 'styleguide/components/Tooltip';

const Container = styled.div<{ mode: Mode }>`
  ${({ theme, mode }) => css`
    height: 40px;
    flex-shrink: 0;
    display: flex;
    gap: ${theme.spaces.s};
    align-items: center;
    justify-content: center;

    ${mode === 'Dark'
      ? css`
          background: ${theme.colors.OnyxGrayD10};
          border-top: 1px solid ${theme.colors.OnyxGrayD20};
        `
      : css`
          border-top: 1px solid ${theme.colors.GlitterGray};
          background: ${theme.colors.GlitterGrayL20};
        `}
  `}
`;

const StyledToggle = styled(Toggle)<{ mode: Mode }>`
  ${({ theme, isActive, mode }) => css`
    border: 0 none;
    background: ${theme.colors.Transparent};

    ${mode === 'Dark'
      ? css`
          svg > path {
            fill: ${isActive ? theme.colors.AquaTeal : theme.colors.GlitterGrayL30};
          }

          &:hover {
            ${!isActive &&
            css`
              svg > path {
                fill: ${theme.colors.GlitterGrayD10};
              }
            `}
          }
        `
      : css`
          svg > path {
            fill: ${isActive ? theme.colors.BrandPurple : theme.colors.OnyxGray};
          }

          &:hover {
            ${!isActive &&
            css`
              svg > path {
                fill: ${theme.colors.OnyxGrayD20};
              }
            `}
          }
        `}
  `}
`;

export const viewports = [
  {
    value: MediaBreakpoint.LG,
    hint: 'View as desktop',
    icon: ComputerIcon,
  },
  {
    value: MediaBreakpoint.MD,
    hint: 'View as tablet',
    icon: TabletIcon,
  },
  {
    value: MediaBreakpoint.SM,
    hint: 'View as mobile portrait',
    icon: MobileIcon,
  },
];

export const ViewportDropdown = () => {
  const viewportBreakpoint = useViewportBreakpoint();
  const setViewportBreakpoint = useSetViewportBreakpoint();
  const eventing = useEventing();
  const { mode } = useModeContext();

  const onClick = (value: MediaBreakpoint) => {
    eventing.track(`editor.media.click.${value}`);
    setViewportBreakpoint(value);
  };

  return (
    <Container data-cy="viewport-breakpoints-container" mode={mode}>
      {viewports.map(({ hint, value, icon }) => (
        <Tooltip key={value} content={hint} position="bottom" showDelay={200}>
          <StyledToggle
            data-cy={`viewport-toggle-${value}`}
            isActive={viewportBreakpoint === value}
            onClick={() => onClick(value)}
            icon={icon}
            mode={mode}
          />
        </Tooltip>
      ))}
    </Container>
  );
};
